import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { ExternalObjectConf } from '@bageltechnologies/bagel-common';
import {
  IntegrationObjectService,
  type IntegrationObjectsFilters,
  type UpsertIntegrationObjectParams,
} from '../api/integration-object-service';
import type { PaginatedResult, PaginationDto } from '../types/common';

export const integrationObjectQueryKeys = {
  all: ['integration-objects'] as const,
  list: (filters: IntegrationObjectsFilters, pagination: PaginationDto = {}) =>
    ['integration-objects', 'list', filters, pagination] as const,
};

export function useIntegrationObjects(
  filters: IntegrationObjectsFilters,
  pagination?: PaginationDto,
): UseQueryResult<PaginatedResult<ExternalObjectConf>> {
  return useQuery({
    queryKey: integrationObjectQueryKeys.list(filters, pagination),
    queryFn: () => IntegrationObjectService.getObjects(filters, pagination),
    enabled: Boolean(filters.accountId),
  });
}

export interface UpsertIntegrationObjectVariables {
  params: UpsertIntegrationObjectParams;
  body: Partial<ExternalObjectConf>;
}

export function useUpsertIntegrationObject(): UseMutationResult<
  ExternalObjectConf,
  Error,
  UpsertIntegrationObjectVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ params, body }: UpsertIntegrationObjectVariables) =>
      IntegrationObjectService.upsertObject(params, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationObjectQueryKeys.all });
    },
  });
}
